// Libs
import React, { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { Collapse, Icon, Divider } from 'antd';

// Own Libs
import * as ui from '@digital/ui';

//Api
import { getAllDiretoriasOptions } from '@app/api/diretoria';
import {
    getAllFiscaisOptions,
    getAllGestoresOptions,
    getAllTenicoMedicaoOptions,
    getAllAdministradoresContratoOptions,
    getAllUsuarioResponsavelRateioOptions,
} from '@app/api/usuario';
import { getAllAreasByDiretoriaOptions } from '@app/api/area';
import { getAllDepartamentoProAreaIdOptions, getAllDepartamentoProDiretoriaIdOptions } from '@app/api/departamento';

//Files

//Utils
import { hasPermission } from '@app/helpers/permissions';
import { getContratoMultiplosGestores, putContratoMultiplosGestores } from '@app/api/contrato';
import { mapToOptions } from '@app/helpers/arrayHelper';
import { isObjEmpty } from '@app/helpers/objHelper';
import { getAllPlanilhaQuantidadeMultiplosGestoresByContrato } from '@app/api/contrato/planilhaQuantidadeContrato';

const { Panel } = Collapse;

interface PanelResponsaveisMrnV2Props {
    contratoId: string;
    isConsolidado?: boolean;
}

interface FormResponsaveisMrn {
    planilhaDeQuantidadeId: string;
    gestorId: string;
    fiscalId: string;
    fiscalAlternativoId: string;
    tecnicoMedicaoId: string;
    administradorContratoId: string;
    administradorContratoAlternativoId: string;
    dataValidaInicio: string;
    dataValidaFinal: string;
    responsavelRateioId: string;
    contratoLegivelParaProcessoNda: string;
    diretoriaId: string;
    possuiArea: boolean;
    possuiDepartamento: boolean;
    areaId: string;
    departamentoId: string;
}

export function PanelResponsaveisMrnV2({
    contratoId,
    isConsolidado
}: PanelResponsaveisMrnV2Props) {

    const [initialValues, setInitialValues] = useState({} as FormResponsaveisMrn);
    const [idDiretoria, setIdDiretoria] = useState<string | undefined>('');
    const [idAreaId, setIdAreaId] = useState<string | undefined>('');
    const [possuiArea, setPossuiArea] = useState(false);
    const [possuiDepartamento, setPossuiDepartamento] = useState(false);
    const [carregarDepartamento, setCarregarDepartamento] = useState<boolean>(false);
    const [areas, setAreas] = useState<ui.OptionProps[]>();
    const [departamentos, setDepartamentos] = useState<ui.OptionProps[]>();
    const [planilhasQuantidade, setPlanilhasQuantidade] = useState<any[]>();

    const [fiscalOptions] = ui.usePromise(getAllFiscaisOptions, [], [] as ui.OptionProps[]);
    const [tecnicoOptions] = ui.usePromise(getAllTenicoMedicaoOptions, [], [] as ui.OptionProps[]);

    const [update, setUpdate] = useState(false);

    const onUpdate = () => { setUpdate(!update) }

    const onSetPq = (pq: FormResponsaveisMrn) => {
        setInitialValues(pq);

        setPossuiArea(pq.possuiArea);
        setPossuiDepartamento(pq.possuiDepartamento);

        handleChangeDiretoria(pq.diretoriaId);
        onClicArea(pq.areaId);
    }

    useEffect(() => {

        getContratoMultiplosGestores(contratoId)
            .then(async (data: any) => {    
                let pqs = data.dados;            
                if(!hasPermission(['SGC_CONTRATO_VIZUALIZAR_TODAS_SEÇOES_PQ_ASSOCIADAS'])){
                    let result = await getAllPlanilhaQuantidadeMultiplosGestoresByContrato(contratoId);
                    pqs = data.dados.filter((item:any)=>{
                        let aux = result && result.find((aux:any)=>{
                            return aux.id === item.planilhaDeQuantidadeId
                        })

                        if(aux && aux.isResponsavel){
                            return true;
                        }else{
                            return false;
                        }
                    })
                    
                }

                setPlanilhasQuantidade(pqs);
                
                const pq1 = pqs.reduce((prev: any, current: any) => {
                    const prevNum = Number(prev.planilhaDeQuantidadeNome.replace(/\D/g, ''));
                    const currentNum = Number(current.planilhaDeQuantidadeNome.replace(/\D/g, ''));
                    return prevNum < currentNum ? prev : current;
                });

                if (isObjEmpty(initialValues)) {
                    onSetPq(pq1);
                } else {
                    const pqSel = [...data.dados].find(f => f.planilhaDeQuantidadeId == initialValues.planilhaDeQuantidadeId);
                    onSetPq(pqSel);
                }
            });

    }, [update, isConsolidado]);

    useEffect(() => {
        if (idDiretoria) {
            getAllAreasByDiretoriaOptions(idDiretoria).then(data => {
                let areas = [...data];
                setAreas(areas);
            });
        }
    }, [idDiretoria]);

    useEffect(() => {
        setDepartamentos([]);

        const areaSelecionada = idAreaId && idAreaId !== 'na' ? idAreaId : undefined;
        if (!areaSelecionada && !idDiretoria) return;

        let cancelado = false;
        const buscaDepartamentos = areaSelecionada
            ? getAllDepartamentoProAreaIdOptions(areaSelecionada, true, initialValues.departamentoId)
            : getAllDepartamentoProDiretoriaIdOptions(idDiretoria, true, initialValues.departamentoId);

        buscaDepartamentos.then(data => {
            if (!cancelado) setDepartamentos([...data]);
        });

        return () => {
            cancelado = true;
        };
    }, [idDiretoria, idAreaId, initialValues.departamentoId]);

    const onSubmitForm = (values: any) => {
        putContratoMultiplosGestores(contratoId, values)
            .then(data => {
                onUpdate();
            })
    }

    const limparCampoDepartamento = () => {
        const departamento = document.getElementById('departamentoId');
        if (departamento) {
            const botaoLimparDoDepartamento = departamento.getElementsByClassName('ant-select-selection__clear');
            if (botaoLimparDoDepartamento.length > 0) botaoLimparDoDepartamento[0].click();
        }
    };

    const limparCampoArea = () => {
        const area = document.getElementById('areaId');
        if (area) {
            const botaoLimparDaArea = area.getElementsByClassName('ant-select-selection__clear');
            if (botaoLimparDaArea.length > 0) botaoLimparDaArea[0].click();
        }
    };

    const handleChangeDiretoria = (value: string) => {
        setIdDiretoria(value);
    };

    const onClicArea = (id: string) => {
        setIdAreaId(id);
    };

    var memoForm = useMemo(() => {

        return (
            <ui.MasterDetailForm
                onSubmit={onSubmitForm}
                initialValues={initialValues}
                validationSchema={Yup.object().shape({
                    diretoriaId: Yup.string().required('Diretoria deve ser informada'),
                    gestorId: Yup.string().required('Gestor deve ser informado'),
                    fiscalId: Yup.string().required('Fiscal deve ser informado'),
                    planilhaDeQuantidadeId: Yup.string().required('A Planilha deve ser informada'),
                })}>
                <ui.Widget
                    actions={[
                        hasPermission(['CONTRATO_INF_COMPLEMENTAR_EDITAR']) && (
                            <ui.FormSubmitButton noIcon type="primary">
                                <ui.FormatText id="ui.commons.save" uppercase />
                            </ui.FormSubmitButton>
                        ),
                    ]}>

                    <ui.FormSelect
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, item: any) => {
                            return item.props.children.props.id.toString().toLowerCase().startsWith(input.toLowerCase());
                        }}
                        label="Planilha de Quantidade"
                        name="planilhaDeQuantidadeId"
                        placeholder="Selecione uma opção"
                        options={mapToOptions(planilhasQuantidade, 'planilhaDeQuantidadeId', 'planilhaDeQuantidadeNome')}
                        span={4}
                        allowClear
                        onSelect={(value: any) => {
                            if (planilhasQuantidade) {
                                const pq = planilhasQuantidade.find(f => f.planilhaDeQuantidadeId == value);
                                if (pq) {
                                    onSetPq(pq);
                                }
                            }
                        }}
                    />

                    <Divider />

                    <ui.FormSelect
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input: any, item: any) => {
                            return item.props.children.props.id.toString().toLowerCase().startsWith(input.toLowerCase());
                        }}
                        label="Gestor"
                        name="gestorId"
                        placeholder="Selecione uma opção"
                        required
                        getOptions={getAllGestoresOptions}
                        span={3}
                    />
                    <ui.FormSelect
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, item: any) => {
                            return item.props.children.props.id.toString().toLowerCase().startsWith(input.toLowerCase());
                        }}
                        label="Fiscal"
                        name="fiscalId"
                        required
                        options={fiscalOptions}
                        span={3}
                        placeholder="Selecione uma opção"
                    />
                    <ui.FormSelect
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, item: any) => {
                            return item.props.children.props.id.toString().toLowerCase().startsWith(input.toLowerCase());
                        }}
                        label="Fiscal Alternativo"
                        name="fiscalAlternativoId"
                        placeholder="Selecione uma opção"
                        options={fiscalOptions}
                        span={3}
                        allowClear
                    />
                    <ui.FormSelect
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, item: any) => {
                            return item.props.children.props.id.toString().toLowerCase().startsWith(input.toLowerCase());
                        }}
                        label="Técnico Medição"
                        name="tecnicoMedicaoId"
                        placeholder="Selecione uma opção"
                        options={tecnicoOptions}
                        span={3}
                        allowClear
                    />

                    <ui.FormSelect
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, item: any) => {
                            return item.props.children.props.id.toString().toLowerCase().startsWith(input.toLowerCase());
                        }}
                        label="Adm. Contrato"
                        placeholder="Selecione uma opção"
                        name="administradorContratoId"
                        span={3}
                        allowClear
                        getOptions={getAllAdministradoresContratoOptions}
                    />

                    <ui.FormSelect
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, item: any) => {
                            return item.props.children.props.id.toString().toLowerCase().startsWith(input.toLowerCase());
                        }}
                        label="Adm. Contrato Alternativo"
                        name="administradorContratoAlternativoId"
                        span={3}
                        placeholder="Selecione uma opção"
                        allowClear
                        getOptions={getAllAdministradoresContratoOptions}
                    />

                    <ui.FormDatePicker
                        format="DD/MM/YYYY"
                        label="Data Validade Início"
                        name="dataValidaInicio"
                        span={3}
                    />
                    <ui.FormDatePicker
                        format="DD/MM/YYYY"
                        label="Data Validade Fim"
                        name="dataValidaFinal"
                        span={3}
                    />

                    <ui.FormSelect
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, item: any) => {
                            return item.props.children.props.id.toString().toLowerCase().startsWith(input.toLowerCase());
                        }}
                        label="Responsável Pelo Rateio"
                        name="responsavelRateioId"
                        span={3}
                        placeholder="Selecione uma opção"
                        allowClear
                        getOptions={getAllUsuarioResponsavelRateioOptions}
                    />
                    {hasPermission(['CONTRATO_GERAL_ATIVAR_INATIVAR']) ? (
                        <ui.FormSwitch
                            label="Requer aprovações NDA?"
                            name="contratoLegivelParaProcessoNda"
                            span={4}
                        />
                    ) : (
                        <> </>
                    )}

                    <Divider />

                    <ui.FormSelect
                        label="Diretoria"
                        name="diretoriaId"
                        placeholder="Selecione uma opção"
                        required
                        getOptions={getAllDiretoriasOptions}
                        onChange={(value: any) => handleChangeDiretoria(value)}
                        span={6}
                    />

                    <ui.FormSwitch
                        onClick={value => {
                            if (possuiArea) {
                                limparCampoArea();
                                setCarregarDepartamento(!carregarDepartamento);
                            }
                            setPossuiArea(!possuiArea);
                        }}
                        label="Possui Área?"
                        name="possuiArea"
                        span={3}
                    />

                    <ui.FormSwitch
                        onClick={value => {
                            if (possuiDepartamento) {
                                limparCampoDepartamento();
                            }
                            setPossuiDepartamento(!possuiDepartamento);
                        }}
                        label="Possui Departamento?"
                        name="possuiDepartamento"
                        span={3}
                    />

                    <ui.FormSelect
                        label="Área"
                        name="areaId"
                        id="areaId"
                        options={areas}
                        span={6}
                        placeholder=""
                        onChange={value => onClicArea(value as string)}
                        allowClear
                        disabled={!possuiArea}
                        required={possuiArea}
                    />

                    <ui.FormSelect
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, item: any) => {
                            return item.props.children.props.id.toString().toLowerCase().startsWith(input.toLowerCase());
                        }}
                        label="Departamento"
                        name="departamentoId"
                        id="departamentoId"
                        options={departamentos}
                        span={6}
                        placeholder=""
                        allowClear
                        required={possuiDepartamento}
                        disabled={!possuiDepartamento}
                    />
                </ui.Widget>
            </ui.MasterDetailForm>
        );
    }, [initialValues, carregarDepartamento, possuiArea, possuiDepartamento, idAreaId, idDiretoria, departamentos, areas])

    return (
        <Collapse
            bordered={false}
            expandIcon={() => <Icon type="arrows-right_4" />}
            defaultActiveKey={'responsaveisMRN'}
        >
            <Panel key="responsaveisMRN" header={<span>Responsáveis MRN</span>} style={{ border: 0 }}>
                {memoForm}
            </Panel>
        </Collapse>
    )
}
